import React from 'react';
import {
  alpha,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowForward,
  CheckCircle,
  PendingActions,
  Timeline,
  WarningAmber,
} from '@mui/icons-material';

const trendPoints = [
  { x: 8, y: 82 },
  { x: 22, y: 82 },
  { x: 36, y: 78 },
  { x: 50, y: 76 },
  { x: 64, y: 72 },
  { x: 78, y: 52 },
  { x: 92, y: 49 },
  { x: 106, y: 45 },
  { x: 120, y: 30 },
  { x: 134, y: 26 },
  { x: 148, y: 18 },
  { x: 162, y: 14 },
];

const reviewRows = [
  {
    product: 'Bebida de Aveia',
    formulation: 'NewFormulation-2026',
    from: 'Idea',
    to: 'Prototype',
    review: 'Not reviewed',
    tone: 'warning',
  },
  {
    product: 'Tortilhas Milho',
    formulation: 'Form123',
    from: 'Idea',
    to: 'Prototype',
    review: 'Approved',
    tone: 'success',
  },
  {
    product: 'Papas Aveia',
    formulation: 'Papas Aveia',
    from: 'Scale-up',
    to: 'Production',
    review: 'Rejected',
    tone: 'error',
  },
];

function ReviewStatus({ status, tone }) {
  const styles = {
    success: {
      Icon: CheckCircle,
      color: 'success.dark',
      bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
      borderColor: (theme) => alpha(theme.palette.success.main, 0.28),
    },
    warning: {
      Icon: PendingActions,
      color: 'warning.dark',
      bgcolor: (theme) => alpha(theme.palette.warning.main, 0.14),
      borderColor: (theme) => alpha(theme.palette.warning.main, 0.34),
    },
    error: {
      Icon: WarningAmber,
      color: 'error.main',
      bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
      borderColor: (theme) => alpha(theme.palette.error.main, 0.26),
    },
  };

  const style = styles[tone] || styles.warning;
  const Icon = style.Icon;

  return (
    <Stack
      direction="row"
      spacing={0.45}
      alignItems="center"
      sx={{
        px: 0.75,
        py: 0.35,
        borderRadius: 999,
        border: '1px solid',
        fontSize: 10,
        fontWeight: 800,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        color: style.color,
        bgcolor: style.bgcolor,
        borderColor: style.borderColor,
      }}
    >
      <Icon sx={{ fontSize: 14 }} />
      <Box component="span">{status}</Box>
    </Stack>
  );
}

function StageChip({ label }) {
  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.3,
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 800,
        color: 'primary.main',
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.22),
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

function ManagementFormulationTrackingVisual() {
  const polyline = trendPoints.map((point) => `${point.x},${point.y}`).join(' ');

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 300,
        p: 1.6,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1.7,
            py: 1.2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Timeline sx={{ fontSize: 16 }} />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Product and formulation tracking
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.2 }}
              >
                Trends, review status and stage transitions
              </Typography>
            </Box>
          </Stack>

          <Chip
            label="FLM tracking"
            size="small"
            sx={{
              height: 23,
              fontSize: 10.5,
              fontWeight: 800,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
            }}
          />
        </Stack>

        {/* Body */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1.25fr' },
            minHeight: 235,
          }}
        >
          {/* Trend */}
          <Box
            sx={{
              p: 1.7,
              borderRight: { sm: '1px solid rgba(15, 23, 42, 0.08)' },
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.016),
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                Product creation
              </Typography>

            </Stack>

            <Paper
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: 'background.paper',
                mb: 1.2,
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 170 95"
                sx={{
                  width: '100%',
                  height: 105,
                  display: 'block',
                }}
              >
                {[20, 40, 60, 80].map((y) => (
                  <line
                    key={y}
                    x1="8"
                    x2="162"
                    y1={y}
                    y2={y}
                    stroke="rgba(100, 116, 139, 0.18)"
                    strokeWidth="1"
                  />
                ))}

                <polyline
                  points={polyline}
                  fill="none"
                  stroke="#1976d2"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {trendPoints.slice(-5).map((point, index) => (
                  <circle
                    key={`${point.x}-${point.y}`}
                    cx={point.x}
                    cy={point.y}
                    r={index === 4 ? 3.2 : 2.4}
                    fill="#fff"
                    stroke="#1976d2"
                    strokeWidth="2"
                  />
                ))}
              </Box>

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  Jan
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  Jun
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  Dec
                </Typography>
              </Stack>
            </Paper>

            <Stack direction="row" spacing={0.8}>
              {[
                { label: 'Products', value: '103' },
                { label: 'Formulations', value: '143' },
              ].map((item) => (
                <Paper
                  key={item.label}
                  variant="outlined"
                  sx={{
                    flex: 1,
                    p: 1,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontSize: 10, fontWeight: 800 }}
                  >
                    {item.label}
                  </Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 900, lineHeight: 1.1 }}>
                    {item.value}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Box>

          {/* Review states */}
          <Box sx={{ p: 1.7 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 1,
                fontWeight: 800,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              Formulation review states
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1.15fr 1.05fr 1fr',
                  columnGap: 1,
                  px: 1.25,
                  py: 0.9,
                  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {['Product', 'Stage', 'Review'].map((head) => (
                  <Typography
                    key={head}
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 800, fontSize: 10.4 }}
                  >
                    {head}
                  </Typography>
                ))}
              </Box>

              {reviewRows.map((row, index) => (
                <Box
                  key={`${row.product}-${row.formulation}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.15fr 1.05fr 1fr',
                    columnGap: 1,
                    alignItems: 'center',
                    px: 1.25,
                    py: 0.95,
                    borderBottom: index !== reviewRows.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: index === 0
                      ? (theme) => alpha(theme.palette.warning.main, 0.045)
                      : 'background.paper',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 900,
                        fontSize: 10.8,
                        lineHeight: 1.25,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {row.product}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        fontSize: 9.8,
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {row.formulation}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.45} alignItems="center">
                    <StageChip label={row.from} />
                    <ArrowForward sx={{ fontSize: 13, color: 'text.disabled' }} />
                    <StageChip label={row.to} />
                  </Stack>

                  <ReviewStatus status={row.review} tone={row.tone} />
                </Box>
              ))}
            </Paper>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default ManagementFormulationTrackingVisual;